// src/components/OptimizedImage.tsx
import { useState, useEffect, useCallback } from 'react';
import LazyLoad from 'react-lazyload';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
}

export default function OptimizedImage({ src, alt, className }: OptimizedImageProps) {
  const [imageSrc, setImageSrc] = useState(src || '/images/placeholder.svg');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isExternalUrl, setIsExternalUrl] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleError = useCallback(() => {
    if (retryCount < 3) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        const img = new Image();
        img.src = isExternalUrl ? `${imageSrc}?retry=${retryCount}` : (imageSrc.startsWith('/') ? `${window.location.origin}${imageSrc}?retry=${retryCount}` : `${window.location.origin}/${imageSrc}?retry=${retryCount}`);
        img.onload = () => {
          console.log(`Successfully loaded image: ${img.src}`);
          setIsLoading(false);
        };
        img.onerror = (error) => {
          console.error(`Failed to load image: ${img.src}`, error);
          handleError();
        };
      }, 1000 * Math.pow(2, retryCount));
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, [retryCount, isExternalUrl, imageSrc]);

  useEffect(() => {
    // Skip processing if src is empty
    if (!src || src.trim() === '') {
      console.warn('Empty image source provided to OptimizedImage');
      setImageSrc('/images/placeholder.svg');
      setIsLoading(false);
      return;
    }

    // Reset states when src changes
    setIsLoading(true);
    setHasError(false);
    setRetryCount(0);

    // Check if it's an external URL
    const isExternal = src.startsWith('http://') || src.startsWith('https://');
    setIsExternalUrl(isExternal);

    // If it's an external URL, use it directly
    if (isExternal) {
      console.log(`Loading external image: ${src}`);
      setImageSrc(src);
    } else {
      // Handle relative paths
      const fullPath = src.startsWith('/') ? `${window.location.origin}${src}` : `${window.location.origin}/${src}`;
      console.log(`Loading local image: ${fullPath}`);
      setImageSrc(fullPath);
    }

    // Preload the image
    const img = new Image();
    img.src = isExternal ? src : (src.startsWith('/') ? `${window.location.origin}${src}` : `${window.location.origin}/${src}`);
    
    img.onload = () => {
      console.log(`Successfully loaded image: ${img.src}`);
      setIsLoading(false);
    };
    
    img.onerror = (error) => {
      console.error(`Failed to load image: ${img.src}`, error);
      handleError();
    };
  }, [src, handleError]);

  return (
    <div className="optimized-image-container">
      <LazyLoad 
        height={300} 
        offset={100}
        placeholder={
          <div className={`${className} animate-pulse bg-gray-300 dark:bg-gray-700`} />
        }
      >
        {isLoading ? (
          <div className={`${className} animate-pulse bg-gray-300 dark:bg-gray-700`} />
        ) : hasError ? (
          <div className="error-container">
            <img
              src="/images/placeholder.svg"
              alt={`Failed to load: ${alt}`}
              className={className}
            />
            <div className="text-center text-sm text-red-500 mt-2">
              Image failed to load
            </div>
          </div>
        ) : (
          <img
            src={imageSrc + (retryCount > 0 ? `?retry=${retryCount}` : '')}
            alt={alt}
            className={className}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              console.error(`Runtime error loading image: ${imageSrc}`);
              handleError();
            }}
          />
        )}
      </LazyLoad>
    </div>
  );
}